/**
 * The media of the Lab run's scenes, read the way the Playground reads them (lab-back playground StudioCanvas.vue): a
 * step's image preloaded before it crossfades in (`preload`), a detection mask at most 512 px, its white pixels boxed
 * with a quarter of padding, then tinted coral to lie over the image in `screen` blend (`loadMask`).
 */

/** The Playground's coral (its `--coral-500`, our coral-600) */
const MASK_TINT = '#FF6E51'
/** A pixel belongs to the mask from this red level (a white-on-black mask, softened by the downscale) */
const MASK_THRESHOLD = 96
const MASK_MAX_SIDE = 512

/** A rectangle in fractions of the image, so it holds at any displayed size */
export interface LabMaskBox {
  x: number
  y: number
  w: number
  h: number
}

/** Where the camera stands over the photo's frame: scale first, then the translation, from the top-left corner */
export interface LabCamera {
  scale: number
  tx: number
  ty: number
}

export const LAB_CAMERA_FIT: LabCamera = { scale: 1, tx: 0, ty: 0 }

/**
 * The box around the mask's pixels (RGBA, red channel above the threshold), padded by a quarter of its width — 8 px at
 * least — on every side and kept inside the image; null when the mask is empty.
 */
export const findLabMaskBox = (pixels: ArrayLike<number>, width: number, height: number): LabMaskBox | null => {
  let x0 = width
  let y0 = height
  let x1 = -1
  let y1 = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4] > MASK_THRESHOLD) {
        if (x < x0) x0 = x
        if (x > x1) x1 = x
        if (y < y0) y0 = y
        if (y > y1) y1 = y
      }
    }
  }
  if (x1 < 0) return null
  const pad = Math.max(8, (x1 - x0) * 0.25)
  const left = Math.max(0, x0 - pad)
  const top = Math.max(0, y0 - pad)
  return {
    x: left / width,
    y: top / height,
    w: Math.min(width - left, x1 - x0 + 2 * pad) / width,
    h: Math.min(height - top, y1 - y0 + 2 * pad) / height,
  }
}

/**
 * The Playground's camera on a detection (`bboxTarget`, zoom-out 0.45): the box centred at 45 % of the frame. Unlike
 * the Playground's open canvas, the photo's frame clips: the camera never goes below the frame, and pans no further
 * than the photo still covers it.
 */
export const labCameraOnBox = (box: LabMaskBox, frameWidth: number, frameHeight: number, zoomOut = 0.45): LabCamera => {
  const scale = Math.max(1, Math.min(32, Math.min(1 / box.w, 1 / box.h) * zoomOut))
  const clamp = (value: number, min: number) => Math.min(0, Math.max(min, value))
  return {
    scale,
    tx: clamp(frameWidth / 2 - (box.x + box.w / 2) * frameWidth * scale, frameWidth - frameWidth * scale),
    ty: clamp(frameHeight / 2 - (box.y + box.h / 2) * frameHeight * scale, frameHeight - frameHeight * scale),
  }
}

/** The mask tinted coral (black stays black: it vanishes in `screen` blend) and its box; null when it is empty */
export const loadLabDetectionMask = async (url: string): Promise<{ url: string, box: LabMaskBox } | null> => {
  const image = new Image()
  image.src = url
  await image.decode()
  const k = Math.min(1, MASK_MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight))
  const width = Math.max(1, Math.round(image.naturalWidth * k))
  const height = Math.max(1, Math.round(image.naturalHeight * k))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null
  context.drawImage(image, 0, 0, width, height)
  const box = findLabMaskBox(context.getImageData(0, 0, width, height).data, width, height)
  if (!box) return null
  context.globalCompositeOperation = 'multiply'
  context.fillStyle = MASK_TINT
  context.fillRect(0, 0, width, height)
  context.globalCompositeOperation = 'destination-in'
  context.drawImage(image, 0, 0, width, height)
  return { url: canvas.toDataURL(), box }
}

/**
 * Loads and decodes a step's image before it is shown (the Playground's `preload`): a remote intermediate would
 * otherwise crossfade in empty. Never waits past the timeout and never fails — a slow image shows as it arrives.
 */
export const preloadLabSceneImage = (url: string, timeoutMs = 4000) => new Promise<void>((resolve) => {
  let isDone = false
  const finish = () => {
    if (isDone) return
    isDone = true
    resolve()
  }
  const image = new Image()
  image.onload = finish
  image.onerror = finish
  image.src = url
  image.decode?.().then(finish, () => undefined)
  window.setTimeout(finish, timeoutMs)
})
